import type { Membership, MembershipGroups } from 'pkg/api/models/membership';
import { RoleType } from 'pkg/api/models/membership';
import {
	isPlayer,
	isAdminOrStaff,
	isParent,
} from 'pkg/api/models/membership/guards';

enum MembershipRole {
	Unknown = 0,
	User = 1,
	Admin = 2,
	Staff = 5,
}

export function normalizeRole(role: number | string): RoleType {
	if (typeof role === 'string') {
		if (Object.values(RoleType).includes(role as RoleType)) {
			return role as RoleType;
		}
		return RoleType.Unknown;
	}
	switch (role) {
		case MembershipRole.Admin:
			return RoleType.Admin;
		case MembershipRole.Staff:
			return RoleType.Staff;
		case MembershipRole.User:
			return RoleType.Player;
		default:
			return RoleType.Unknown;
	}
}

export function roleToLegacyNumber(role: RoleType): number {
	switch (role) {
		case RoleType.Admin:
		case RoleType.Owner:
		case RoleType.ThreeSixtyAdmin:
			return MembershipRole.Admin;
		case RoleType.Staff:
			return MembershipRole.Staff;
		case RoleType.Unknown:
			return MembershipRole.Unknown;
		case RoleType.Player:
		default:
			return MembershipRole.User;
	}
}

export function normalizeMembership<T extends { role?: number | string }>(
	raw: T
): T & { role: RoleType } {
	return { ...raw, role: normalizeRole(raw.role) };
}

export function legacyRoleString(membership: Membership): string {
	if (isParent(membership)) {
		return 'parent';
	}

	if (membership.role === RoleType.Player) {
		return 'user';
	}

	return membership.role;
}

export function filterActiveMembership(
	memberships: Membership[],
	activeUserIds: number[],
	activeMembership: Membership
) {
	let contacts = memberships.filter(
		(membership) =>
			!activeUserIds.includes(membership.userId) && membership.user.accountId
	);

	if (activeMembership.targetUserId) {
		contacts = contacts.filter((membership) => !isPlayer(membership));
	}

	return contacts;
}

export function groupByRole(memberships: Membership[]): MembershipGroups {
	const adminOrStaff: Membership[] = [];
	const players: Membership[] = [];
	const parents: Membership[] = [];

	memberships.forEach((membership) => {
		if (isParent(membership)) {
			parents.push(membership);
		} else if (isAdminOrStaff(membership)) {
			adminOrStaff.push(membership);
		} else {
			players.push(membership);
		}
	});

	return {
		adminOrStaff,
		players,
		parents,
	};
}
